import React, { JSX, useContext, useMemo } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import ProviderAffiliationsAccordionResults from "@app/projects/servicing-provider/ProviderAffiliationsAccordion";
import { getClaimsVolumeDefinitions, getEfficiencyAndEffectivenessDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { NPF } from "@app/projects/servicing-provider/types";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import InfoIndicator from "@app/shared/InfoIndicator";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { valueOrDashes } from "@app/shared/utility/valueOrDashes";
import ClaimsProviderInsights from "@app/projects/servicing-provider/ClaimsProviderInsights";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import BenchmarkingFiltersOverview from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BenchmarkingFiltersOverview";
import BenchmarkingProviderFlyoutAddressTable from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BenchmarkingProviderFlyoutAddressTable";
import BlurredProviderAffiliationsFlyoutTable from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BlurredProviderAffiliationsFlyoutTable";
import { useBenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/hooks/useBenchmarkingProviderFlyoutData";
import { BenchmarkingDashboardDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardDetails";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail";

interface BenchmarkingProviderFlyoutProps {
  dashboardDetails: BenchmarkingDashboardDetails;
  formatNetworkList: (networkList: string[], isFlyout?: boolean) => JSX.Element;
}

const BenchmarkingProviderFlyout = (props: BenchmarkingProviderFlyoutProps): JSX.Element => {
  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);

  const payload: NetworkComparisonFiltersPayload | null = useMemo(() => {
    if (!props.dashboardDetails?.npi) {
      return null;
    }

    const formattedPayload = benchmarkingContext.getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersContext.displayedNetworkComparisonFilters));
    formattedPayload.npi = props.dashboardDetails.npi;
    return formattedPayload;
  }, [props.dashboardDetails, benchmarkingContext.getFormattedFiltersPayload, networkComparisonFiltersContext.displayedNetworkComparisonFilters]);

  const { entityType, isLoading: loading, providerFlyoutData: benchmarkingProviderFlyoutData } = useBenchmarkingProviderFlyoutData(benchmarkingFiltersContext.getService(), payload);

  return (
    <React.Fragment>
      <div className="row mb-3">
        <div className="col-md-12">
          <div>
            <h1 className="pl0-imp">
              {props.dashboardDetails.providerName}
            </h1>
            <div>
              NPI: {props.dashboardDetails?.npi}
            </div>
          </div>
        </div>
      </div>

      <BenchmarkingFiltersOverview />

      <AccordionContainer>
        <Accordion
          defaultOpen={true}
          testId={`benchmarking-dashboard--provider-flyout--provider-details-${props.dashboardDetails?.npi}`}
          title="Provider Details"
        >
          {!!props.dashboardDetails && (
            <React.Fragment>
              <div className="row">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">Specialty</label> {
                    (benchmarkingProviderFlyoutData?.specialties || [null]).map((specialty, index) => {
                      return (
                        <div className="mb-3" key={index}>
                          {valueOrDashes(specialty)}
                        </div>)
                    })}
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">Effectiveness (Quality) <InfoIndicator className={"informational-indicator"} tooltipTitle={getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank)} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.effectivenessRank)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">Efficiency (Cost) <InfoIndicator className={"informational-indicator"} tooltipTitle={getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank)} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.efficiencyRank)}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">PCA - Medicare <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.medicareClaimsLabel)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">PCA - Medicaid <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.medicaidClaimsLabel)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">PCA - Commercial <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.commercialClaimsLabel)}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">PCA - Total <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.totalClaimsLabel)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">Network Count <InfoIndicator className={"informational-indicator"} tooltipTitle="Count of selected networks a provider is contracted with" /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.networkCount)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="demibold mb-0">Network List <InfoIndicator className={"informational-indicator"} tooltipTitle="List of all selected networks a provider is contracted with" /></label>
                  <div className="mb-3">
                    {props.formatNetworkList(props.dashboardDetails.networkList)}
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </Accordion>

        <Accordion
          testId={`benchmarking-dashboard--provider-flyout--address-${props.dashboardDetails?.npi}`}
          title="Address"
        >
          <BenchmarkingProviderFlyoutAddressTable
            benchmarkingProviderFlyoutData={benchmarkingProviderFlyoutData}
            loading={loading}
            npi={props.dashboardDetails.npi}
            formatNetworkList={props.formatNetworkList}
          />
        </Accordion>

        <Accordion
          testId={`benchmarking-dashboard--provider-flyout--claims-${props.dashboardDetails?.npi}`}
          title="Claims"
        >
          {!loading && !!benchmarkingProviderFlyoutData ? (
            <div className="servicing-provider-profile">
              <ClaimsProviderInsights
                showTricare={true}
                showVA={true}
                record={benchmarkingProviderFlyoutData as unknown as ServicingProviderDetail}
              />
            </div>
          ) : (
            <LoadingSpinner />
          )}
        </Accordion>

        <Accordion
          testId={`benchmarking-dashboard--provider-flyout--affiliations-${props.dashboardDetails?.npi}`}
          title="Affiliations"
        >
          {benchmarkingFiltersContext.isAffiliationState() ? (
            (!!props.dashboardDetails && !benchmarkingContext.affiliationsVersionLoading) ? (
              <ProviderAffiliationsAccordionResults
                provider={props.dashboardDetails as unknown as ServicingProviderDetail}
                providerAffiliationVersionId={benchmarkingContext.affiliationsVersion ?? 0}
                entityType={entityType}
              />
            ) : (
              <LoadingSpinner />
            )
          ) : (
            <BlurredProviderAffiliationsFlyoutTable />
          )}
        </Accordion>
      </AccordionContainer>
    </React.Fragment>
  );
}

export default BenchmarkingProviderFlyout;
